import { useCallback, useEffect, useRef, useState } from "react";
import { Image as NextUIImage, ScrollShadow } from "@heroui/react";
import AppLogo from "app/assets/logo.png";
import { sidebarLinks } from "./sideBarLinks";
import { Link as RemixLink } from "@remix-run/react";
import { toKebabCase } from "app/utils/fn";
import { IoIosCloseCircleOutline, IoIosArrowDown, IoIosArrowUp } from "react-icons/io";
import DarkModeSwitch from "../DarkModeSwitch";
import { categoryI } from "app/api/category/category.types";

type SidebarProps = {
  sidebarOpen: boolean;
  categories: categoryI[];
  setSidebarOpen: (arg: boolean) => void;
};

export const Sidebar = ({ sidebarOpen, setSidebarOpen, categories }: SidebarProps) => {
  const triggerRef = useRef<HTMLDivElement>(null);
  const sidebarRef = useRef<HTMLDivElement>(null);
  const backdropRef = useRef<HTMLDivElement>(null);
  const firstFocusableRef = useRef<HTMLButtonElement>(null);
  const lastFocusedElement = useRef<Element | null>(null);

  // Close sidebar with useCallback to prevent unnecessary re-renders
  const closeSidebar = useCallback(() => {
    setSidebarOpen(false);
    // Restore focus to the last focused element when sidebar is closed
    if (lastFocusedElement.current instanceof HTMLElement) {
      lastFocusedElement.current.focus();
    }
  }, [setSidebarOpen]);

  const [expandedCategories, setExpandedCategories] = useState<{ [key: string]: boolean }>({});
  const [backUpCategory, setBackUpCategory] = useState([]);
  const toggleCategory = useCallback((id: string) => {
    setExpandedCategories((prev) => ({ ...prev, [id]: !prev[id] }));
  }, []);

  // Apply inert when closed to prevent focus on hidden content (fixes aria-hidden-focus)
  useEffect(() => {
    const el = sidebarRef.current;
    if (el) {
      if (sidebarOpen) {
        el.removeAttribute("inert");
      } else {
        el.setAttribute("inert", "");
      }
    }
  }, [sidebarOpen]);

  useEffect(() => {
    // Save the currently focused element to restore focus later
    if (sidebarOpen) {
      lastFocusedElement.current = document.activeElement;
      // Focus the first focusable element in the sidebar after opening
      setTimeout(() => {
        firstFocusableRef.current?.focus();
      }, 0);
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    const clickOutsideHandler = ({ target }: MouseEvent) => {
      if (
        sidebarOpen &&
        !sidebarRef.current?.contains(target as Node) &&
        !triggerRef.current?.contains(target as Node) &&
        !backdropRef.current?.contains(target as Node)
      ) {
        closeSidebar();
      }
    };

    const keyHandler = ({ key }: KeyboardEvent) => {
      if (sidebarOpen && key === "Escape") {
        closeSidebar();
      }
    };

    document.addEventListener("click", clickOutsideHandler);
    document.addEventListener("keydown", keyHandler);

    return () => {
      document.removeEventListener("click", clickOutsideHandler);
      document.removeEventListener("keydown", keyHandler);
      document.body.style.overflow = "";
    };
  }, [sidebarOpen, closeSidebar]);

  return (
    <>
      {sidebarOpen && (
        <div
          ref={backdropRef}
          className="fixed inset-0 bg-black bg-opacity-50 z-[100]"
          onClick={closeSidebar}
          role="presentation"
          aria-label="Dialog overlay"
        ></div>
      )}

      <aside
        ref={sidebarRef}
        role="dialog"
        aria-modal="true"
        aria-label="Sidebar Navigation"
        aria-hidden={!sidebarOpen}
        tabIndex={sidebarOpen ? 0 : -1}
        className={`fixed xl:hidden w-4/5 sm:w-[60%] md:w-2/5 left-0 top-0 z-[1000] flex flex-col items-start justify-start overflow-y-clip bg-white dark:bg-slate-900 transition-transform duration-300 ease-linear h-screen ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="w-full flex justify-start">
          <div className="flex w-full items-center justify-between px-4 py-4">
            <RemixLink to="/" tabIndex={sidebarOpen ? undefined : -1}>
              <NextUIImage
                src={AppLogo}
                loading="lazy"
                width={240}
                height={60}
                alt="Logo"
                className="h-10 sm:h-12 max-w-44 sm:max-w-64 2xl:h-20"
              />
            </RemixLink>
            <div tabIndex={sidebarOpen ? undefined : -1}>
              <DarkModeSwitch />
            </div>
            <button
              ref={firstFocusableRef}
              onClick={closeSidebar}
              aria-label="Close sidebar"
              tabIndex={sidebarOpen ? undefined : -1}
            >
              <IoIosCloseCircleOutline className="text-2xl text-gray/70 dark:text-gray-100" />
            </button>
          </div>
        </div>

        <div className="w-full flex flex-col overflow-y-auto duration-300 ease-linear">
          <ScrollShadow hideScrollBar as="nav" className="p-4 h-[calc(100vh - 7.15rem)]" size={140}>
            <div className="overflow-x-hidden w-full space-y-4 pl-2">
              <h4 className="border-b border-dashed pb-2 text-sm font-semibold">ALL PRODUCTS</h4>
              <ul className="space-y-2">
                {[...categories, ...(categories.length === 0 ? backUpCategory : [])].map(
                  (category) => (
                    <li key={`category-${category._id}`} className="pb-2 border-b border-dashed">
                      <div className="flex gap-x-2 justify-between items-center">
                        <RemixLink
                          to={`/category/${toKebabCase(category.name)}`}
                          prefetch="intent"
                          className="text-sm uppercase block w-full"
                          onClick={closeSidebar}
                          tabIndex={sidebarOpen ? undefined : -1}
                        >
                          <span>{category.name}</span>{" "}
                        </RemixLink>
                        {category.subCategory && category.subCategory.length > 0 && (
                          <button
                            // Use button for better accessibility rather than a div
                            onClick={(e) => {
                              e.stopPropagation();
                              e.nativeEvent.stopImmediatePropagation();
                              toggleCategory(category._id);
                            }}
                            className="ml-2 text-2xl"
                            aria-label={`Toggle subcategories for ${category.name}`}
                            aria-expanded={expandedCategories[category._id] || false}
                            tabIndex={sidebarOpen ? undefined : -1}
                          >
                            {expandedCategories[category._id] ? (
                              <IoIosArrowUp />
                            ) : (
                              <IoIosArrowDown />
                            )}
                          </button>
                        )}
                      </div>
                      {expandedCategories[category._id] && category.subCategory && (
                        <ul className="pl-4 mt-2 space-y-1">
                          {category.subCategory.map((subCat) => (
                            <li key={`subCategory-${subCat._id}`} className="pb-1">
                              <RemixLink
                                to={`/category/${toKebabCase(category.name)}/${toKebabCase(
                                  subCat.name,
                                )}`}
                                prefetch="intent"
                                className="text-sm uppercase block"
                                onClick={closeSidebar}
                                tabIndex={sidebarOpen ? undefined : -1}
                              >
                                <span>{subCat.name}</span>{" "}
                              </RemixLink>
                            </li>
                          ))}
                        </ul>
                      )}
                    </li>
                  ),
                )}
              </ul>
              <h4 className="border-b border-dashed pb-4 text-sm font-semibold">MENU</h4>
              <ul className="flex flex-col space-y-1 !mb-28">
                {sidebarLinks.map((link) => (
                  <li key={link.pathname} className="pb-2 border-b border-dashed">
                    <RemixLink
                      prefetch="intent"
                      to={link.pathname}
                      className="text-xs uppercase"
                      tabIndex={sidebarOpen ? undefined : -1}
                    >
                      <span>{link.title}</span>
                    </RemixLink>
                  </li>
                ))}
              </ul>
            </div>
          </ScrollShadow>
        </div>
      </aside>
    </>
  );
};
